/**
 * La navigation de la Feuille (chantier nettoyage-feuille, ticket 02) : quelle fiche est ouverte,
 * si elle l'a été sur place plus bas, ce qu'on a coché au rond le temps de la visite, et ce
 * qu'une arrivée demande. Une règle sans React ; le crochet qui la tient :
 * `projets/useNavigationDeLaFeuille.ts`. Avant, quatre `useEffect` de remise à zéro dans
 * `projets/Feuille.tsx` et un `lieu.state` lu comme un objet libre.
 */

/**
 * Ce qu'une arrivée sur une feuille peut demander, posé dans l'état de la navigation par ceux
 * qui y envoient (« Projets », la Journée, le tuto, `useAllerALaFiche`, la feuille elle-même).
 */
export interface ArriveeSurLaFeuille {
  /** D'où l'on vient de remonter : la carte teinte cette branche-là. */
  venantDe?: string;
  /** La fiche à ouvrir en arrivant (« Fais-le pour moi » du tuto, la fiche d'une bulle lointaine). */
  ouvrirLaFiche?: string;
  /** Montrer la Carte : là où une partie de l'Aide commence (ticket 17 de retours-testeurs). */
  enCarte?: boolean;
}

/** L'arrivée, lue dans l'état de la navigation : ce qui n'a pas la bonne forme est laissé. */
export function lireLArrivee(etat: unknown): ArriveeSurLaFeuille {
  if (typeof etat !== 'object' || etat === null) return {};
  const { venantDe, ouvrirLaFiche, enCarte } = etat as Record<string, unknown>;
  return {
    ...(typeof venantDe === 'string' && venantDe ? { venantDe } : {}),
    ...(typeof ouvrirLaFiche === 'string' && ouvrirLaFiche ? { ouvrirLaFiche } : {}),
    ...(enCarte === true ? { enCarte } : {}),
  };
}

export interface NavigationDeLaFeuille {
  /** La feuille où l'on est, et la clé de l'arrivée déjà lue (une par navigation). */
  feuille: string;
  cle: string;
  /** La bulle dont la fiche est ouverte. */
  fiche: string | null;
  /**
   * La feuille où la fiche a été ouverte sur place plus bas (une bulle dépliée, une étape de
   * « Ses étapes », une ligne d'« Ici, tu peux faire ») : sa mère se cherche alors dans ce qui
   * est lu. Seulement sur cette feuille-là : en changeant de feuille, elle ne compte plus.
   */
  lointainesSur: string | null;
  /**
   * Au téléphone, ce qu'on vient de cocher au rond reste à sa place le temps de la visite
   * (chantier `telephone-projets-fiche`, ticket 02) ; on repart à vide en changeant de feuille.
   */
  gardees: ReadonlySet<string>;
}

export type GesteDeNavigation =
  /** Une adresse lue : une autre feuille, ou une nouvelle arrivée sur la même. */
  | { sorte: 'arriver'; feuille: string; cle: string; arrivee: ArriveeSurLaFeuille }
  /** La fiche d'une bulle de la feuille, du centre, d'une sœur du centre. */
  | { sorte: 'ouvrir'; id: string }
  /** La fiche d'une bulle plus bas que la feuille, ouverte sur place. */
  | { sorte: 'ouvrir-sur-place'; id: string }
  | { sorte: 'fermer' }
  /** Le rond touché au téléphone : la bulle reste à sa place. */
  | { sorte: 'garder'; id: string };

export function navigationDeDepart(feuille: string, cle: string, arrivee: ArriveeSurLaFeuille): NavigationDeLaFeuille {
  return { feuille, cle, fiche: arrivee.ouvrirLaFiche ?? null, lointainesSur: null, gardees: new Set() };
}

export function naviguerDansLaFeuille(nav: NavigationDeLaFeuille, geste: GesteDeNavigation): NavigationDeLaFeuille {
  switch (geste.sorte) {
    case 'arriver': {
      if (geste.feuille === nav.feuille && geste.cle === nav.cle) return nav;
      /*
       * Une autre feuille : ce qui était gardé et ouvert sur place repart à vide. La fiche
       * ouverte, elle, reste demandée : la nouvelle feuille la montre si elle la porte (la fiche
       * d'une étape, en remontant d'un cran), sinon rien ne s'affiche.
       */
      const ailleurs = geste.feuille !== nav.feuille;
      return {
        feuille: geste.feuille,
        cle: geste.cle,
        fiche: geste.arrivee.ouvrirLaFiche ?? nav.fiche,
        lointainesSur: ailleurs ? null : nav.lointainesSur,
        gardees: ailleurs ? new Set() : nav.gardees,
      };
    }
    case 'ouvrir':
      return { ...nav, fiche: geste.id };
    case 'ouvrir-sur-place':
      return { ...nav, fiche: geste.id, lointainesSur: nav.feuille };
    case 'fermer':
      return { ...nav, fiche: null, lointainesSur: null };
    case 'garder':
      return { ...nav, gardees: new Set(nav.gardees).add(geste.id) };
  }
}

/** La fiche ouverte l'a été sur place plus bas, sur cette feuille-ci. */
export function ficheOuverteSurPlace(nav: NavigationDeLaFeuille): boolean {
  return nav.fiche !== null && nav.lointainesSur === nav.feuille;
}
